import React from "react"
import { NavLink } from "react-router-dom"
import { ui } from "../shared/styles/uiSystem"
import OdontoFlowLogo from "../shared/brand/OdontoFlowLogo"

const sections = [
  {
    title: "Operación",
    items: [
      { to: "/dashboard", label: "Dashboard", code: "DB" },
      { to: "/agenda", label: "Agenda", code: "AG" },
      { to: "/patients", label: "Pacientes", code: "PA" }
    ]
  },
  {
    title: "Clínica",
    items: [
      { to: "/treatment-plans", label: "Planes", code: "PL" },
      { to: "/clinical-history", label: "Historia clínica", code: "HC" }
    ]
  },
  {
    title: "Finanzas",
    items: [
      { to: "/billing", label: "Cobros", code: "CO" },
      { to: "/cash-closing", label: "Caja diaria", code: "CJ" },
      { to: "/financial-executive", label: "Finanzas ejecutivas", code: "FE" }
    ]
  },
  {
    title: "Administración",
    items: [
      { to: "/settings", label: "Configuración", code: "CF" }
    ]
  }
]

function SectionTitle({ children, compact }) {
  if (compact) return null

  return (
    <div
      style={{
        ...ui.sidebarSectionLabel,
        padding: "12px 12px 8px",
        marginTop: 6
      }}
    >
      {children}
    </div>
  )
}

function SidebarItem({ item, compact }) {
  return (
    <NavLink
      to={item.to}
      title={item.label}
      style={({ isActive }) => ({
        ...(isActive ? ui.sidebarItemActive : ui.sidebarItem),
        justifyContent: compact ? "center" : "flex-start"
      })}
    >
      {({ isActive }) => (
        <>
          <span
            style={isActive ? ui.sidebarIconBadgeActive : ui.sidebarIconBadge}
          >
            {item.code}
          </span>

          {!compact ? (
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {item.label}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  )
}

function SidebarPlanCard({ compact, subscriptionLabel, subscriptionMeta, subscriptionTone }) {
  if (compact) return null

  const tone = subscriptionTone || {
    bg: "rgba(37,99,235,0.12)",
    border: "rgba(147,197,253,0.28)",
    color: "#bfdbfe"
  }

  return (
    <NavLink
      to="/settings/subscription"
      style={{
        textDecoration: "none",
        margin: "12px 12px 0",
        padding: "10px 11px",
        borderRadius: 16,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(148,163,184,0.20)",
        display: "grid",
        gap: 4,
        boxShadow: "0 12px 26px rgba(0,0,0,0.12)"
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 950,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "rgba(226,232,240,0.62)"
        }}
      >
        Plan de cuenta
      </div>

      <div
        style={{
          display: "inline-flex",
          width: "fit-content",
          maxWidth: "100%",
          padding: "4px 8px",
          borderRadius: 999,
          background: tone.bg,
          border: `1px solid ${tone.border}`,
          color: tone.color,
          fontSize: 11.5,
          fontWeight: 950,
          lineHeight: 1.1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {subscriptionLabel || "Plan activo"}
      </div>

      {subscriptionMeta ? (
        <div
          style={{
            color: "rgba(226,232,240,0.68)",
            fontSize: 11,
            fontWeight: 750,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {subscriptionMeta}
        </div>
      ) : null}
    </NavLink>
  )
}

function SidebarUserFooter({ compact, user, logout }) {
  if (compact || !user) return null

  return (
    <div
      style={{
        padding: "10px 12px 14px",
        borderTop: "1px solid rgba(148,163,184,0.16)",
        display: "grid",
        gap: 8
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "32px minmax(0, 1fr)",
          gap: 9,
          alignItems: "center"
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 999,
            background: "linear-gradient(135deg, #2563eb, #4f46e5)",
            color: "#ffffff",
            display: "grid",
            placeItems: "center",
            fontWeight: 950,
            fontSize: 12.5
          }}
        >
          {String(user?.name || "U").trim().charAt(0).toUpperCase()}
        </div>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "#ffffff",
              fontSize: 12,
              fontWeight: 900,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {user?.name || "Usuario"}
          </div>
          <div style={{ color: "rgba(226,232,240,0.55)", fontSize: 10.5, fontWeight: 700 }}>
            Sesión activa
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={logout}
        style={{
          minHeight: 34,
          width: "100%",
          borderRadius: 12,
          border: "1px solid rgba(226,232,240,0.18)",
          background: "rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.92)",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 900
        }}
      >
        Cerrar sesión
      </button>
    </div>
  )
}

export default function Sidebar({
  compact = false,
  user = null,
  logout = null,
  subscriptionLabel = "Plan activo",
  subscriptionMeta = "",
  subscriptionTone = null
}) {
  return (
    <aside
      style={{
        ...ui.sidebarShell,
        width: compact ? 88 : 252,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div style={ui.sidebarHeader}>
        {compact ? (
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 14,
              background: "linear-gradient(135deg, rgba(36,87,214,0.95), rgba(79,70,229,0.95))",
              display: "grid",
              placeItems: "center",
              color: "#FFFFFF",
              fontWeight: 900,
              fontSize: 14,
              boxShadow: "0 12px 22px rgba(36,87,214,0.18)"
            }}
            title="OdontoFlow"
          >
            <OdontoFlowLogo
              variant="icon"
              theme="dark"
              size="md"
              showText={false}
            />
          </div>
        ) : (
          <OdontoFlowLogo
            theme="dark"
            size="sm"
            showDescriptor={false}
            style={{
              maxWidth: "100%",
            }}
          />
        )}
      </div>

      <SidebarPlanCard
        compact={compact}
        subscriptionLabel={subscriptionLabel}
        subscriptionMeta={subscriptionMeta}
        subscriptionTone={subscriptionTone}
      />

      <nav
        style={{
          padding: compact ? "10px 8px 14px" : "12px 10px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          overflowY: "auto",
          flex: 1,
          minHeight: 0
        }}
      >
        {sections.map((section) => (
          <div key={section.title}>
            <SectionTitle compact={compact}>{section.title}</SectionTitle>

            <div
              style={{
                display: "grid",
                gap: 6
              }}
            >
              {section.items.map((item) => (
                <SidebarItem
                  key={item.to}
                  item={item}
                  compact={compact}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <SidebarUserFooter compact={compact} user={user} logout={logout} />
    </aside>
  )
}
